import { CollapsableCard } from '@app/app/_components/CollapsableCard/CollapsableCard';
import { VehicleInformationPanel } from '../_sections/steps/VehicleInformationStep/VehicleInformationPanel';
import { CollapsablePanel } from '@app/app/_components/CollapsablePanel/CollapsablePanel';
import { ReadOnlyField } from '@app/app/_components/ReadOnlyField/ReadOnlyField';
import { useTranslations } from 'next-intl';
import { VehicleInformation } from '@app/domain/model/eshop/transport/VehicleInformation';
import { FormState } from '@app/lib/form/contracts/FormState';
import { PurchaseTransportInsuranceResult } from '@app/service/eshop/transport/actions/purchaseTransportInsurance';
import { CriterionOption } from '@app/domain/model/eshop/transport/CriterionOption';
import { Message } from '@app/app/_components/Message/Message';
import Link from 'next/link';
import { SummaryEditButton } from './SummaryEditButton';
import { AppParameters } from '@app/infrastructure/parameters/model/AppParameters';

interface Props {
    vehicleInformation: VehicleInformation;
    formState: FormState<PurchaseTransportInsuranceResult>;
    selectedPurpose: Nullable<{ name: string; code: string }>;
    ownerAge: Nullable<CriterionOption>;
    experienceOtherDrivers: Nullable<CriterionOption>;
    territory: Nullable<{ name: string; code: string }>;
    handleOpenUpdateModal: (step: string) => void;
    isCompany: boolean;
    parameters: AppParameters;
}

export function VehicleSummaryCard({
    vehicleInformation,
    formState,
    selectedPurpose,
    ownerAge,
    experienceOtherDrivers,
    territory,
    handleOpenUpdateModal,
    isCompany,
    parameters,
}: Props) {
    const t = useTranslations();

    return (
        <>
            <CollapsableCard label={t('eshop.transport.vehicleSummary')}>
                <div className="pb-2">
                    <VehicleInformationPanel
                        collapsable={false}
                        defaultCollapsed={false}
                        vehicleInformation={vehicleInformation}
                        formState={formState}
                    />
                </div>
                {!isCompany && (
                    <CollapsablePanel
                        label={t('eshop.transport.drivingExperience')}
                        collapsable={false}
                        addon={
                            <SummaryEditButton onClick={handleOpenUpdateModal} stepToNavigate="step-1-3">
                                {t('general.edit')}
                            </SummaryEditButton>
                        }>
                        <div className="px-6 py-2">
                            <ReadOnlyField label={t('general.since')}>
                                {formState.data?.formData.drivingExperience}
                            </ReadOnlyField>
                        </div>
                    </CollapsablePanel>
                )}
                <CollapsablePanel
                    label={t('eshop.transport.purposeOfTheVehicle')}
                    collapsable={false}
                    addon={
                        <SummaryEditButton onClick={handleOpenUpdateModal} stepToNavigate="step-1-2">
                            {t('general.edit')}
                        </SummaryEditButton>
                    }>
                    <div className="px-6 py-2">
                        <ReadOnlyField label={t('eshop.transport.answerLabel')}>{selectedPurpose?.name}</ReadOnlyField>
                    </div>
                </CollapsablePanel>
                <CollapsablePanel
                    label={t('eshop.transport.otherDrivers')}
                    collapsable={false}
                    addon={
                        <SummaryEditButton onClick={handleOpenUpdateModal} stepToNavigate="step-1-3">
                            {t('general.edit')}
                        </SummaryEditButton>
                    }>
                    <div className="px-6 py-2">
                        <ReadOnlyField label={t('eshop.transport.ownerAge')}>{ownerAge?.name}</ReadOnlyField>
                        <ReadOnlyField label={t('eshop.transport.experienceOfOtherDrivers')}>
                            {experienceOtherDrivers?.name}
                        </ReadOnlyField>
                    </div>
                </CollapsablePanel>
                <CollapsablePanel label={t('eshop.transport.territory')} collapsable={false}>
                    <div className="flex flex-col gap-3 px-6 py-2">
                        <ReadOnlyField label={t('eshop.transport.territoryCountriesLabel')}>
                            {territory?.name}
                        </ReadOnlyField>
                        <Message variant="hint">
                            {t.rich('eshop.transport.territoryInfoMessage', {
                                link: chunks => (
                                    <Link
                                        target="_blank"
                                        href={parameters.eshopInfoUrls.greenCourseInfo}
                                        className="text-primary-500 underline">
                                        {chunks}
                                    </Link>
                                ),
                            })}
                        </Message>
                    </div>
                </CollapsablePanel>
            </CollapsableCard>
        </>
    );
}
